import * as React from 'react'
import { useContext, useEffect } from 'react'
import { Box, Card, CardMedia, Divider, List, ListItem, ListItemButton, Paper, Typography, useMediaQuery, useTheme } from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import EuroIcon from '@mui/icons-material/Euro'
import { ReservationContext } from '../../context/ReservationContext'
import { formatDate } from '../../utils/date'
import { GoogleLocation } from '../GoogleLocation'
import PropTypes from 'prop-types'

export function ShowSelection({ setPreviousStepCompleted }) {
  const theme = useTheme()
  const { shows, showId, setShowId, scheduleId, setScheduleId, setCategories } = useContext(ReservationContext)
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm')) // xs‑sm

  const handleScheduleSelect = (selectedScheduleId, showId) => {
    // If clicking on already selected item, unselect it
    if (scheduleId === selectedScheduleId) {
      setPreviousStepCompleted(false)
      setShowId(-1)
      setScheduleId(-1)
    } else {
      setShowId(showId)
      setScheduleId(selectedScheduleId)
      const selectedShow = shows.find((show) => show.id === showId)
      if (selectedShow?.prices) {
        setCategories(selectedShow.prices.map((price) => ({ ...price, quantity: 0 })))
      } else {
        setCategories([])
      }
      setPreviousStepCompleted(true)
    }
  }

  useEffect(() => {
    if (showId > 0 && scheduleId > 0) {
      setPreviousStepCompleted(true)
    }
  }, [showId, scheduleId, setPreviousStepCompleted])

  return (
    <Paper
      elevation={3}
      sx={{
        minHeight: '400px',
        maxHeight: { xs: '50vh', sm: '60vh' },
        overflowY: 'auto',
        p: { xs: 1, sm: 2 },
        borderRadius: 10
      }}
    >
      <Typography variant='h5' sx={{ mb: 1, fontWeight: 'bold', color: 'title.main' }}>
        1. Choix du spectacle
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {shows.map((show) => (
          <Card key={show.id} variant='outlined' sx={{ borderRadius: 2, boxShadow: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
              <CardMedia
                component='img'
                image={'/' + show.image}
                alt={show.title}
                sx={{
                  width: { xs: '100%', md: '35%' },
                  height: { xs: 'auto', md: '100%' },
                  objectFit: 'cover',
                  minHeight: { xs: '200px', md: 'auto' },
                  borderTopLeftRadius: 8,
                  borderBottomLeftRadius: { xs: 0, md: 8 },
                  borderTopRightRadius: { xs: 8, md: 0 }
                }}
              />
              <Box
                sx={{
                  flex: 1,
                  p: { xs: 0, sm: 2 },
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <Typography variant='h5' sx={{ mb: 1, fontWeight: 'bold', color: 'primary.main' }}>
                  {show.title}
                </Typography>
                <GoogleLocation show={show} />

                <Typography
                  variant='h6'
                  sx={{
                    mb: 2,
                    fontWeight: 'medium',
                    display: { xs: 'flex', md: 'none' }
                  }}
                >
                  <EuroIcon color='primary' sx={{ mr: 1 }} />
                  <Typography color='primary' sx={{ fontWeight: 'bold' }}>
                    {show.price}
                  </Typography>
                </Typography>
                <Typography variant='subtitle1' sx={{ mb: 0, fontWeight: 'medium', color: 'title.main' }}>
                  Dates disponibles:
                </Typography>
                <List>
                  {show.schedules.map((schedule) => (
                    <React.Fragment key={schedule.id}>
                      <ListItem disablePadding>
                        <ListItemButton
                          selected={scheduleId === schedule.id && showId === show.id}
                          onClick={() => handleScheduleSelect(schedule.id, show.id)}
                          sx={{
                            py: 1,
                            '&.Mui-selected': {
                              backgroundColor: 'backgroundColors.selected',
                              '&:hover': {
                                backgroundColor: 'backgroundColors.selectedHover'
                              }
                            },
                            '&:hover': {
                              backgroundColor: 'backgroundColors.hover'
                            }
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                            <CalendarTodayIcon
                              color='action'
                              sx={{
                                mr: 2,
                                display: { xs: 'none', md: 'block' },
                                color: 'primary.main'
                              }}
                            />
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography variant='body1' sx={{ fontWeight: 'medium' }}>
                                {formatDate(schedule.date, isSmallScreen)} à {schedule.time}
                              </Typography>
                              <Typography variant='body2' sx={{ color: 'primary.main' }}>
                                {show.title}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: { xs: 'none', md: 'flex' },
                                alignItems: 'center'
                              }}
                            >
                              <EuroIcon color='primary' sx={{ mr: 1 }} />
                              <Typography variant='h6' color='primary' sx={{ fontWeight: 'bold' }}>
                                {show.price}
                              </Typography>
                            </Box>
                            <Box sx={{ ml: 2 }}>
                              {scheduleId === schedule.id ? (
                                <CheckCircleIcon color='primary' />
                              ) : (
                                <RadioButtonUncheckedIcon color='action' />
                              )}
                            </Box>
                          </Box>
                        </ListItemButton>
                      </ListItem>
                      <Divider />
                    </React.Fragment>
                  ))}
                </List>
              </Box>
            </Box>
          </Card>
        ))}
      </Box>
    </Paper>
  )
}

ShowSelection.propTypes = {
  setPreviousStepCompleted: PropTypes.func.isRequired
}
